import {
  SubmitEventHandler,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import {
  FOOTER_DEFAULT_NEWSLETTER_PROPS,
} from "@/ui/components/navigation/Footer/Footer.config";
import type {
  TNewsletterSignupProps,
} from "@/ui/components/sections/marketing/NewsletterSignUp";
import {
  useNewsletterSubscribe,
} from "apps/website/hooks/useNewsletterSubscribe";
import type { TFunctionOf } from "apps/website/types/i18n";

import { DisplayState } from "@/constants/state";

const SOURCE = "Footer";

type TSignupStatus = "ready" | "processing" | "complete" | "error";

function getStatusCopy(
  status: TSignupStatus,
  t: TFunctionOf<"Components.Marketing.NewsletterSignUp">,
): Pick<TNewsletterSignupProps, "title" | "body"> {
  switch (status) {
    case "complete":
      return { title: t("completeTitle"), body: t("completeBody") };
    case "error":
      return { body: t("errorMessage") };
    default:
      return {};
  }
}

export function useFooterNewsletterSignup(): TNewsletterSignupProps {
  const t = useTranslations("Components.Marketing.NewsletterSignUp");
  const [ email, setEmail ] = useState("");
  const [ status, setStatus ] = useState<TSignupStatus>("ready");
  const isLockedRef = useRef(false);
  const subscribe = useNewsletterSubscribe(SOURCE);

  const onSubmit: SubmitEventHandler<HTMLFormElement> = useCallback((event) => {
    event.preventDefault();

    if (isLockedRef.current) {
      return;
    }

    isLockedRef.current = true;
    setStatus("processing");

    subscribe(email)
      .then(() => setStatus("complete"))
      .catch(() => {
        isLockedRef.current = false;
        setStatus("error");
      });
  }, [ email, subscribe ]);

  return {
    ...FOOTER_DEFAULT_NEWSLETTER_PROPS,
    ...getStatusCopy(status, t),
    onSubmit,
    button: {
      state: status === "processing" ? DisplayState.LOADING : DisplayState.READY,
    },
    input: {
      ...FOOTER_DEFAULT_NEWSLETTER_PROPS.input,
      value: email,
      required: true,
      disabled: status === "complete",
      onChange: (event) => {
        setEmail(event.target.value);
        setStatus((current) => (current === "error" ? "ready" : current));
      },
    },
  };
}
